import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { GetFacilityAdmin } from "api/rpc/facilityAdmin/client/admin";

import { CustomerContextType, CustomerContextState, BookingCountType } from "./contextTypes";

import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetCustomer, GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";
// import { IPreferences } from "api/rpc/2024-04/customer/customer";
import { ICustomerPreferences } from "redux/reducers/models/customer";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { GetGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { GetTotalBookings } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { GetPaymentOptions } from "api/rpc/2024-04/facilityAdmin/facility/paymentOption";
import { GetRainChecks } from "api/rpc/2024-04/facilityAdmin/payment/rainCheck";
import { THIS_YEAR } from "components/form/calendarField/helpers";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";

const defaultState: CustomerContextType = {
  state: {
    customer: null,
    preferences: null,
    editState: null,
    notes: null,
    bookings: null,
    bookingCounts: [],
    memberships: null,
    reservationBookings: null,
    houseAccounts: null,
    selectedHouseAccount: null,
    accountTransactions: null,
    accountStatements: null, // assigning NULL disables HouseAccount inputs
    accountScheduledPayments: null,
    accountingReferenceTypes: null,
    paymentMethods: null,
    paymentAmount: "",
    giftCards: null,
    rainChecks: null,
    ticketStubs: null,
    countries: null,
    customerTypes: null,
    cartTypes: null,
    facilityAdminFilters: null,
    registerFilters: null,
    paymentOptionFilters: null,
  },
  updateState: null,
  reloadPaymentMethods: null,
  loadCustomer: null,
  refreshGiftCards: null,
  loadGiftCardsWithFilters: null,
};

/* ACTUAL CONTEXT */
const CustomerContext = createContext<CustomerContextType>(defaultState);

/* CONTEXT PROVIDER */
type CustomerProviderProps = {
  customerId: number;
  children: any;
};
export interface IGiftCardFilter {
  offset: number;
  limit: number;
}

interface IBookingTotalsByYear {
  [year: string]: {
    checked?: number;
    unchecked?: number;
    no_show_defer?: number;
    no_show_charge?: number;
    no_show_weather?: number;
  };
}

export const useCustomerContext = () => useContext(CustomerContext);

/** Loads necessary customer data with Global-Loader based off ID passed.  This is data available on first-load, other state defined in specified tabs. */
export default function CustomerContextProvider(props: CustomerProviderProps) {
  const [state, setState] = useState<CustomerContextState>({ ...defaultState.state });
  const { t } = useTranslation();

  function updateState(newCustomerState: Partial<CustomerContextState>) {
    setState((cur: CustomerContextState) => {
      return { ...cur, ...newCustomerState };
    });
  }

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadCustomer(props.customerId, source.token);
    void loadAccountingReferenceTypes(source.token);

    // Payment methods and cards
    void reloadPaymentMethods(props.customerId, false, source.token);
    void loadGiftCardsWithFilters(
      { offset: 0, limit: 50 },
      props.customerId,
      source.token,
    ); /** Initial gift card load */
    // void refreshGiftCards(props.customerId, false, source.token);
    void loadRainChecks(props.customerId, source.token);

    // Drop filters
    void loadRegisterFilters(source.token);
    void loadFacilityAdminFilters(source.token);
    void loadTotalBookings(props.customerId);
    void loadFacilityPaymentOptions(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadCustomer(customerId: number, token?: CancelToken) {
    const customerRes = await GetCustomer(
      {
        id: customerId,
        memberships: true,
        preferences: true,
        "default-address-line": true,
        "address-lines": true,
      },
      true,
      token,
    );

    if (customerRes.status !== StatusCode.OK) {
      return;
    }

    let preferences: Partial<ICustomerPreferences> = customerRes.data[0].preferences;

    // Set default preferences if preferences don't exist
    if (preferences === null) {
      preferences = {
        customer_id: customerId,
        marketing_email: false,
        booking_confirmation_email: true,
        booking_reminder_email: true,
        booking_update_email: true,
        booking_cancellation_email: true,
      };
    }

    console.log("Customer", customerRes.data[0]);

    updateState({
      customer: customerRes.data[0],
      preferences: preferences,
      editState: customerRes.data[0],
    });
  }

  async function loadAccountingReferenceTypes(token?: CancelToken) {
    const accountingResponse = await GetAccountingReference(
      {
        group: "accounts_receivable",
      },
      false,
      token,
    );

    if (accountingResponse.status !== StatusCode.OK) {
      return;
    }

    const accountingReferenceTypes: any[] = [];

    accountingReferenceTypes.push({ label: t("None"), value: 0 });

    if (accountingResponse?.data?.length !== undefined && accountingResponse.data.length > 0) {
      const accountingReferenceTypesResponse = accountingResponse.data.map(
        (accountingReference: { title?: string; reference_number?: string | null; id?: number }) => {
          return {
            label: formatAccountingReferenceLabel(
              accountingReference.title ?? "",
              accountingReference.reference_number,
            ),
            value: accountingReference.id ?? 0,
          };
        },
      );

      accountingReferenceTypes.push(...accountingReferenceTypesResponse);
    }

    console.log("AccountingRefType ", accountingReferenceTypes);

    updateState({ accountingReferenceTypes: accountingReferenceTypes });
  }

  async function reloadPaymentMethods(customerId: any, useGlobalLoader = true, token?: CancelToken) {
    if (state.paymentMethods !== null) {
      updateState({ paymentMethods: null });
    }

    const cardRes = await GetCustomerPaymentMethod({ customer_id: customerId }, useGlobalLoader, token);
    if (cardRes.status !== StatusCode.OK) {
      return;
    }

    updateState({ paymentMethods: cardRes.data });
  }

  async function refreshGiftCards(customer_id: number, useGlobalLoader = false, token?: CancelToken) {
    if (state.giftCards !== null) {
      updateState({ giftCards: null });
    }

    const giftCardRes = await GetGiftCard({ customer_id: customer_id }, useGlobalLoader, token);
    if (giftCardRes.status !== StatusCode.OK) {
      return;
    }

    console.log("gift cards ", giftCardRes); //debug
    updateState({ giftCards: giftCardRes.data.reverse() }); // Newest displayed first
  }

  async function loadGiftCardsWithFilters(filters: IGiftCardFilter, customer_id: number, token?: CancelToken) {
    if (state.giftCards !== null) {
      updateState({ giftCards: null });
    }

    const getGiftCardParams = {
      limit: filters.limit,
      offset: filters.offset,
      customer_id: customer_id,
    };

    const giftCardRes = await GetGiftCard(getGiftCardParams, true, token);
    if (giftCardRes.status !== StatusCode.OK) {
      return;
    }

    console.log("filtered gift cards", giftCardRes); //debug
    updateState({ giftCards: giftCardRes.data.reverse() }); // Newest displayed first
  }
  async function loadRainChecks(customer_id: number, token?: CancelToken) {
    if (state.rainChecks !== undefined) {
      updateState({ rainChecks: undefined });
    }

    const rainChecksRes = await GetRainChecks({ customer_id: customer_id }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (rainChecksRes.status !== StatusCode.OK) {
      updateState({ rainChecks: [] });
      return;
    }

    updateState({ rainChecks: rainChecksRes.data });
  }

  async function loadRegisterFilters(token?: CancelToken) {
    const registersRes = await GetRegisters(null, false, token);
    if (registersRes.status !== StatusCode.OK) {
      console.log("Register error: ", registersRes?.message);
      return;
    }

    updateState({ registerFilters: registersRes.data });
  }

  async function loadFacilityAdminFilters(token?: CancelToken) {
    const adminsRes = await GetFacilityAdmin(null, false, token);
    if (adminsRes.status !== StatusCode.OK) {
      console.log("Admin error: ", adminsRes?.message);
      return;
    }

    updateState({ facilityAdminFilters: adminsRes.data });
  }

  async function loadTotalBookings(customerId: number, token?: CancelToken) {
    const bookingRes = await GetTotalBookings({ customer_id: customerId }, false, token);
    if (bookingRes.status !== StatusCode.OK) {
      console.log("Total bookings error");
      return;
    }

    const bookingCounts: Array<BookingCountType> = [];

    const bookingsByYear: IBookingTotalsByYear = bookingRes.data as IBookingTotalsByYear;
    const keysByYear = Object.keys(bookingsByYear);

    keysByYear.map(year => {
      let total = 0;
      let noShow = 0;
      let weatherNoShow = 0;
      total += Number(bookingsByYear[year].checked ?? 0) + Number(bookingsByYear[year].unchecked ?? 0);
      noShow += Number(bookingsByYear[year].no_show_defer ?? 0) + Number(bookingsByYear[year].no_show_charge ?? 0);
      weatherNoShow += bookingsByYear[year].no_show_weather ?? 0;
      bookingCounts.push({
        year: year,
        count: total,
        no_shows: noShow,
        weather_no_shows: weatherNoShow,
      });
    });

    if (!bookingCounts.some(countYear => countYear.year === THIS_YEAR.toString())) {
      bookingCounts.push({
        year: THIS_YEAR.toString(),
        count: 0,
        no_shows: 0,
        weather_no_shows: 0,
      });
    }

    updateState({ bookingCounts: bookingCounts });
  }

  async function loadFacilityPaymentOptions(token?: CancelToken) {
    const res = await GetPaymentOptions(null, false, token);

    if (res.status !== StatusCode.OK) {
      console.log("options error", res?.message);
      return;
    }

    updateState({ paymentOptionFilters: res.data });
  }

  return (
    <CustomerContext.Provider
      value={{ state, updateState, reloadPaymentMethods, loadCustomer, refreshGiftCards, loadGiftCardsWithFilters }}
    >
      {props.children}
    </CustomerContext.Provider>
  );
}
